import axios from "@core/plugins/axios.plugin"
import { createUniqueId } from "@core/helpers"
import { handleServiceCall } from "@services/helpers"
import type { OptimTurpeAnalyseResponse } from "@modules/courtage/types/optim-turpe"
import type { OptimTurpeDashboardWorkflowAnalysesResponse } from "@modules/courtage/types/optim-turpe-dashboard"

export class OptimTurpeService {
    /**
     * Synthèse parc : workflows et analyses agrégées (dashboard optimisation contractuelle).
     * @param controller - Annulation requête.
     */
    async getOptimDashboard(
        controller?: AbortController
    ): Promise<OptimTurpeDashboardWorkflowAnalysesResponse> {
        return handleServiceCall<OptimTurpeDashboardWorkflowAnalysesResponse>(
            async () => {
                const { data } = await axios.get(
                    `/api/metier/v5/optimturpe/workflowanalyses`,
                    {
                        signal: controller?.signal,
                    }
                )
                return data
            }
        )
    }

    /**
     * Récupère les analyses TURPE pour une source.
     * @param sourceId - Identifiant de la source.
     * @param controller - Contrôleur d'annulation.
     */
    async getOptimAnalyseBySource(
        sourceId: string,
        controller?: AbortController
    ): Promise<OptimTurpeAnalyseResponse> {
        return handleServiceCall<OptimTurpeAnalyseResponse>(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/optimturpe/source/${sourceId}`,
                {
                    signal: controller?.signal,
                }
            )

            return data
        })
    }

    /**
     * Lance une nouvelle analyse TURPE pour une source.
     * @param params - Paramètres de lancement d'analyse.
     * @param controller - Contrôleur d'annulation.
     */
    async createOptimAnalyse(
        {
            sourceId,
            buildingId,
            dateFrom,
            dateTo,
            eventName,
        }: {
            sourceId: string
            buildingId: string
            dateFrom: string
            dateTo: string
            eventName: string
        },
        controller?: AbortController
    ): Promise<{ task_id: string; event_name: string }> {
        return handleServiceCall(async () => {
            const { data } = await axios.post(
                `/api/metier/v5/optimturpe/source/${sourceId}`,
                {
                    _id_building: buildingId,
                    date_from: dateFrom,
                    date_to: dateTo,
                    event_name: eventName,
                },
                {
                    signal: controller?.signal,
                }
            )

            return {
                task_id: data?.task_id ?? data?.data?.task_id ?? "",
                event_name: data?.event_name ?? data?.data?.event_name ?? "",
            }
        })
    }

    /**
     * Lance la génération du rapport PDF d’une analyse (POST `/v5/analyse/:id/rapport`, même route que legacy / IoT).
     * @param analyseId - Identifiant Mongo de l’analyse (`_id`).
     * @param controller - Annulation requête.
     */
    async generateOptimTurpeAnalyseReport(
        analyseId: string,
        controller?: AbortController
    ): Promise<{ task_id: string; event_name: string }> {
        return handleServiceCall(async () => {
            const event_name = `analyse.optimturpe.rapport.${analyseId}.${createUniqueId()}`

            const { data } = await axios.post(
                `/api/metier/v5/analyse/${analyseId}/rapport`,
                { event_name },
                {
                    signal: controller?.signal,
                }
            )

            const taskId = data?.task_id ?? data?.data?.task_id ?? ""
            const returnedEventName =
                data?.event_name ?? data?.data?.event_name ?? event_name

            return {
                task_id: taskId,
                event_name: returnedEventName,
            }
        })
    }

    /**
     * Supprime une analyse TURPE (`DELETE /v5/optimturpe/analyse/:id`).
     * @param analyseId - Identifiant Mongo de l’analyse (`_id`).
     * @param controller - Annulation requête.
     */
    async deleteOptimAnalyse(
        analyseId: string,
        controller?: AbortController
    ): Promise<void> {
        return handleServiceCall(async () => {
            await axios.delete(
                `/api/metier/v5/optimturpe/analyse/${analyseId}`,
                {
                    signal: controller?.signal,
                }
            )
        })
    }
}
